import {createLayoutPatch, LAYOUT_TYPES} from "@/scripts/common/layoutConfigurations.js";
import {HeroContentFlowEnum, HeroLayoutTypeEnum, SectionTypesEnum} from "@/scripts/common/enums.js";

export default {
    selectSection(state, section) {
        const sectionIndex = section ? state.project.sections.findIndex(s => s._id === section._id) : -1;
        if(sectionIndex !== -1) {
            state.selectedSection = state.project.sections[sectionIndex];
            return;
        }
        state.selectedSection = section ? section : null;
    },
    selectSectionById(state, sectionId) {
        state.selectedSection = state.project.sections.find(s => s._id === sectionId);
    },
    updateSection(state, updatedSection) {
        const index = state.project.sections.findIndex(s => s._id === updatedSection._id);

        if (index !== -1) {
            state.project.sections.splice(index, 1, updatedSection);
        }

        if (state.selectedSection && state.selectedSection._id === updatedSection._id) {
            state.selectedSection = updatedSection;
        }
    },
    patchSelectedSection(state, payload) {
        if (!state.selectedSection) return;

        this.deepMerge(state.selectedSection, payload);
    },
    patchSectionById(state, { sectionId, payload }) {
        const index = state.project.sections.findIndex(s => s._id === sectionId);

        if (index > -1) {
            const section = state.project.sections[index];
            this.deepMerge(section, payload);
        }
    },
    setSectionCustomDesign(state, { sectionId, design }) {
        const section = state.project?.sections?.find(s => s._id === sectionId);
        if (section) section.customDesign = design;
    },
    patchSectionByPath(state, { sectionId, path, value }) {
        const section = state.project.sections.find(s => s._id === sectionId);
        if (!section) return;

        const keys = path.split(".");
        let target = section;
        for (let i = 0; i < keys.length - 1; i++) {
            if (!target[keys[i]]) return;
            target = target[keys[i]];
        }
        target[keys[keys.length - 1]] = value;
    },
    setTextAlignmentInSection(state, { sectionId, columnIndex, align }) {
        const section = state.project.sections.find(s => s._id === sectionId);
        if (!section) return;

        const updateAlignment = (text) => {
            if (!text) return;
            text.style = {
                ...text.style,
                textAlign: align
            };
        };

        if (columnIndex !== null && section.columns) {
            section.columns.forEach(column => updateAlignment(column.text));
        } else {
            updateAlignment(section.text);
        }
    },
    // insert new column, logo, stat, process step, testimonial
    insertSectionItem(state, { sectionId, arrayName, item, index }) {
        const section = state.project.sections.find(s => s._id === sectionId);
        if (!section || !Array.isArray(section[arrayName])) return;

        section[arrayName].splice(index, 0, item);
    },
    // delete column, logo, stat, process step, testimonial
    removeSectionItem(state, { sectionId, arrayName, index }) {
        const section = state.project.sections.find(s => s._id === sectionId);
        if (!section || !Array.isArray(section[arrayName]) || (section && !section[arrayName][index])) return;

        section[arrayName].splice(index, 1);
    },
    updateSectionArray(state, { sectionId, arrayName, newArray }) {
        const section = state.project.sections.find(s => s._id === sectionId);
        if (!section || !Array.isArray(newArray)) return;

        section[arrayName] = [...newArray];
    },
    deleteTextModule(state, { sectionId }) {
        let section = state.project.sections.find(s => s._id === sectionId);
        if (!section) return;

        section.contentSettings = {
            ...section.contentSettings,
            showText: false,
            showMedia: section.type === SectionTypesEnum.ACCORDION
                ? false
                : section.type !== SectionTypesEnum.CONTACT_FORM
                    ? true
                    : section.contentSettings.showMedia
        }

        const layoutType = section.type === SectionTypesEnum.ACCORDION
            ? LAYOUT_TYPES.ACCORDION_FULL_WIDTH
            : section.type === SectionTypesEnum.CONTACT_FORM
            ? (section.contentSettings.showMedia
                ? LAYOUT_TYPES.CONTACT_FORM_ORIGINAL
                : LAYOUT_TYPES.CONTACT_FORM_CENTERED)
            : LAYOUT_TYPES.MEDIA_FULL_WIDTH

        section.styleSettings = {
            ...section.styleSettings,
            boxAlign: section.type === SectionTypesEnum.ACCORDION
                ? "left-top"
                : layoutType === LAYOUT_TYPES.CONTACT_FORM_CENTERED
                    ? "center"
                    : "left"
        }

        section.layoutSettings = createLayoutPatch(section.layoutSettings,  layoutType);
    },
    deleteMediaModule(state, { sectionId }) {
        let section = state.project.sections.find(s => s._id === sectionId);
        if (!section) return;

        section.contentSettings = {
            ...section.contentSettings,
            showText: section.type === SectionTypesEnum.ACCORDION
                ? false
                : section.type !== SectionTypesEnum.CONTACT_FORM
                    ? true
                    : section.contentSettings.showText,
            showMedia: false
        }

        const layoutType = section.type === SectionTypesEnum.ACCORDION
            ? LAYOUT_TYPES.ACCORDION_FULL_WIDTH
            : section.type === SectionTypesEnum.CONTACT_FORM && section.contentSettings.showText
            ? LAYOUT_TYPES.CONTACT_FORM_AND_TEXT
            : section.type === SectionTypesEnum.CONTACT_FORM
                ? LAYOUT_TYPES.CONTACT_FORM_CENTERED
                : LAYOUT_TYPES.TEXT_DEFAULT

        section.styleSettings = {
            ...section.styleSettings,
            boxAlign: section.type === SectionTypesEnum.ACCORDION
                ? "left-top"
                : layoutType === LAYOUT_TYPES.CONTACT_FORM_CENTERED
                    ? "center"
                    : "left"
        }

        if(section.media.mockupSettings) {
            section.media.mockupSettings = null;
        }

        section.layoutSettings = createLayoutPatch(section.layoutSettings, layoutType)
    },
    deleteHeroMediaModule(state) {
        const hero = state.portfolio?.portfolioHero;
        if (!hero?.layoutSettings) return;

        const isSidebarLayout = hero.layoutSettings.layoutType === HeroLayoutTypeEnum.SIDEBAR;
        const layoutConfig = isSidebarLayout ? LAYOUT_TYPES.HERO_SIDEBAR_TEXT : LAYOUT_TYPES.TEXT_DEFAULT;
        const layoutType = isSidebarLayout ? HeroLayoutTypeEnum.SIDEBAR : HeroLayoutTypeEnum.MODULAR;
        const layoutSettingsPatch = createLayoutPatch(hero.layoutSettings, layoutConfig);
        const layoutSettings = {
            ...layoutSettingsPatch,
            layoutType,
            contentFlow: HeroContentFlowEnum.HORIZONTAL
        };

        hero.contentSettings = {
            ...hero.contentSettings,
            showText: true,
            showMedia: false
        };

        hero.styleSettings = {
            ...hero.styleSettings,
            boxAlign: "left"
        };

        hero.layoutSettings = layoutSettings;
    },

    setSectionModules(state, { sectionId, modules }) {
        const section = state.project?.sections?.find((s) => s._id === sectionId || s.id === sectionId);
        const ensureLayoutSettings = (section) => {
            if (!section.layoutSettings) section.layoutSettings = {};
            if (!Array.isArray(section.layoutSettings.modules)) {
                section.layoutSettings.modules = [];
            }
            return section;
        };

        if (!section) return;
        ensureLayoutSettings(section);
        // Replace reference (simple & common)
        section.layoutSettings.modules = Array.isArray(modules) ? modules.slice() : [];
    },

}
